import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Wrench, Heart } from 'lucide-react';
import { stats, aboutImages } from '../mock';

const About = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              About
              <br />
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Carz Street
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Premium car service excellence since 1999
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-4 block">
                Our Story
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Passion for Premium Cars
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Founded in 1999, Carz Street began with a simple mission: to provide premium car owners with service quality that matches their vehicle's excellence.
                </p>
                <p>
                  Over the past 26 years, we've grown from a small workshop to a trusted multi-brand service center, serving over 2 lakh satisfied customers across Ahmedabad and Gujarat.
                </p>
                <p>
                  Our team of certified technicians brings decades of combined experience, ensuring your premium vehicle receives the expert care it deserves.
                </p>
                <p className="text-red-600 font-semibold italic">
                  "Drive in with problems & Drive out with peace of mind."
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src={aboutImages[0]}
                alt="Carz Street multi-brand car service workshop in Ahmedabad"
                loading="lazy"
                decoding="async"
                className="rounded-2xl shadow-2xl w-full h-64 object-cover"
              />
              <img
                src={aboutImages[1]}
                alt="Certified Carz Street technicians at work"
                loading="lazy"
                decoding="async"
                className="rounded-2xl shadow-2xl w-full h-64 object-cover mt-8"
              />
              <img
                src={aboutImages[2]}
                alt="Premium car service at Carz Street"
                loading="lazy"
                decoding="async"
                className="rounded-2xl shadow-2xl w-full h-64 object-cover col-span-2"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-white/90 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: 'Excellence',
                description: 'We strive for perfection in every service we provide'
              },
              {
                icon: Users,
                title: 'Customer First',
                description: 'Your satisfaction is our top priority'
              },
              {
                icon: Wrench,
                title: 'Expertise',
                description: 'Certified technicians with years of experience'
              },
              {
                icon: Heart,
                title: 'Integrity',
                description: 'Honest, transparent, and reliable service always'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;